import request from '@/utils/request'
import type * as projectType from '@/typings/project'
import type * as portfolioType from '@/typings/portfolio'
import type { CommonRes } from '@/typings'
import type { TableDataType } from '@/components/ProjectContent/ProjectPortfolio/components/FinancialTable/type'
import {
  IDynamicTable,
  IGoldStandardSettingInfoResponse,
  PortfolioReviewGetResponse,
  IGetDealPortfolioForLeadsResponse,
  IPortfolioReviewItemType,
  IPortfolioReviewTodo
} from '@/typings/portfolio'
import type { IPortfolioPostInvestmentEventSave } from '@/typings/portfolio'
import type { IPortfolioPostInvestmentEvent } from '@/typings/portfolio'
import type {
  IMemoListForPortfolioExitReplyMemo,
  IPortfolioExit,
  IPortfolioExitSave,
  IPortfolioReviewAttachmentFile
} from '@/typings/portfolio'
import { IProjectName } from '@/typings/project'
import { CompanyStructureType } from '@/constants'

// Portfolio项目列表（卡片）v1.17.1版本
export async function getPortfolioCardProjectList(params: projectType.MemoSearchParams) {
  return request('/api/v2/project/portfolio/search_my_portfolio_card', {
    method: 'POST',
    data: params
  })
}

interface IGetPortfolioInfoRequest {
  project_id: number
}
// 项目 - Portfolio详情 - 获取详情
export async function getPortfolioInfo(params: IGetPortfolioInfoRequest) {
  return request('/api/v2/project/portfolio/get', {
    method: 'get',
    data: params
  })
}

interface IUpdatePortfolioStatusRequest {
  project_id: number
  portfolio_status: number
  remark: string
}
// 项目 - Portfolio详情 - 更改状态
export async function updatePortfolioStatus(params: IUpdatePortfolioStatusRequest) {
  return request('/api/v2/project/portfolio/update_status', {
    method: 'post',
    data: params
  })
}

interface IUpdatePortfolioOwnerRequest {
  project_id: number
  pm_id: number
  partner_id: number
  associate_sponsor_uid: number
}
// 项目 - Portfolio详情 - 重新分配
export async function updatePortfolioOwner(params: IUpdatePortfolioOwnerRequest) {
  return request('/api/v2/project/portfolio/update_owner', {
    method: 'post',
    data: params
  })
}

interface IUpdatePortfolioRequest {
  portfolio: portfolioType.IPortfolioBaseInfo
}

// 项目 - Portfolio详情 - 更新
export async function updatePortfolio(params: IUpdatePortfolioRequest) {
  return request('/api/v2/project/portfolio/update', {
    method: 'post',
    data: params
  })
}

interface IAddPortfolioIssuesRequest {
  portfolio_issue: portfolioType.IPortfolioIssue
}
// 项目 - Portfolio详情 - 新增投后待解决事项
export async function addPortfolioIssues(params: IAddPortfolioIssuesRequest) {
  return request('/api/v2/project/portfolio/issue_add', {
    method: 'post',
    data: params
  })
}

interface IUpdatePortfolioIssuesRequest {
  portfolio_issue: portfolioType.IPortfolioIssue
}
// 项目 - Portfolio详情 - 更新投后待解决事项
export async function updatePortfolioIssues(params: IUpdatePortfolioIssuesRequest) {
  return request('/api/v2/project/portfolio/issue_update', {
    method: 'post',
    data: params
  })
}

interface IDeletePortfolioIssuesRequest {
  id: number
}
// 项目 - Portfolio详情 - 删除投后待解决事项
export async function deletePortfolioIssues(params: IDeletePortfolioIssuesRequest) {
  return request('/api/v2/project/portfolio/issue_delete', {
    method: 'post',
    data: params
  })
}

interface IGetPortfolioManageRequest {
  project_id: number
}
// 项目 - Portfolio详情 - 获取投后管理
export async function getPortfolioManage(params: IGetPortfolioManageRequest) {
  return request('/api/v2/project/portfolio/manage_get', {
    method: 'get',
    data: params
  })
}

interface IAddPortfolioManageRequest {
  portfolio_manage: portfolioType.IPortfolioManageType
}
// 项目 - Portfolio详情 - 新增投后管理
export async function addPortfolioManage(params: IAddPortfolioManageRequest) {
  return request('/api/v2/project/portfolio/manage_add', {
    method: 'post',
    data: params
  })
}

interface IUpdatePortfolioManageRequest {
  portfolio_manage: portfolioType.IPortfolioManageType
}
// 项目 - Portfolio详情 - 更新投后管理
export async function updatePortfolioManage(params: IUpdatePortfolioManageRequest) {
  return request('/api/v2/project/portfolio/manage_update', {
    method: 'post',
    data: params
  })
}
export async function deletePortfolioManage(params: { id: number }) {
  return request('/api/v2/project/portfolio/manage_delete', {
    method: 'post',
    data: params
  })
}

interface IAddPortfolioRequest {
  portfolio: portfolioType.PortfolioBaseInfo
  project: projectType.ProjectSimpleType
}
// 项目 - 新建Portfolio
export async function addPortfolio(params: IAddPortfolioRequest) {
  return request('/api/v2/project/portfolio/create_portfolio_project', {
    method: 'post',
    data: params
  })
}
/**
 * 工作台 - 后台Portfolio列表 - 导出Portfolio信息（issue 116 / ADR-111 决策 8）
 * GET，query = 后台列表同一套筛选参数（去 page / size），返回 xlsx（两个 sheet）；前端 window.open 下载。
 * 功能点 tbl_menu 1001018 portfolio_info_export。
 */
export const PORTFOLIO_INFO_EXPORT_PATH = '/api/v2/project/portfolio/portfolio_info_export'

// 工作台 - 后台Portfolio列表
export async function getBackPortfolioList(params: any) {
  return request('/api/v2/project/portfolio/search_back_portfolio', {
    method: 'post',
    data: params
  })
}

// 工作台 - 合伙人Portfolio
export async function getPartnerPortfolisList(params: any) {
  return request('/api/v2/project/portfolio/search_partner_portfolio', {
    method: 'post',
    data: params
  })
}
// 工作台 - 我的Portfolio
export async function getPmPortfolisList(params: any) {
  return request('/api/v2/project/portfolio/search_my_portfolio', {
    method: 'post',
    data: params
  })
}

// Memo详情 - Portfolio Review - 列表
interface IGetPortfolioReviewListRequest {
  project_id?: number
  ids?: string[]
}
export async function getPortfolioReviewList(params: IGetPortfolioReviewListRequest) {
  return request<CommonRes<{ list: IPortfolioReviewItemType[]; project_list: IProjectName[] }>>(
    '/api/v2/project/portfolio/review_list',
    {
      method: 'GET',
      data: params
    }
  )
}

//  获取Portfolio Review - 详情
interface IGetPortfolioReviewInfoRequest {
  project_id?: number
  id?: number
  is_need_draft?: number //默认为1: true
  is_need_project?: number //默认为1: true
  is_need_newest_memo?: number //默认为1: true
  is_need_portfolio?: number
}
export async function getPortfolioReviewInfo(params: IGetPortfolioReviewInfoRequest) {
  return request<CommonRes<PortfolioReviewGetResponse>>('/api/v2/project/portfolio/review_get', {
    method: 'GET',
    data: params
  })
}

interface IAddPortfolioReviewRequest {
  portfolio_review: portfolioType.IPortfolioReviewDetailType
  is_draft?: boolean
}
// Memo详情 - Portfolio Review - 新增
export async function addPortfolioReview(params: IAddPortfolioReviewRequest) {
  return request('/api/v2/project/portfolio/review_add', {
    method: 'post',
    data: params
  })
}

interface IUpdatePortfolioReviewRequest {
  portfolio_review: portfolioType.IPortfolioReviewDetailType
  is_draft?: boolean
}
// Memo详情 - Portfolio Review - 编辑
export async function updatePortfolioReview(params: IUpdatePortfolioReviewRequest) {
  return request('/api/v2/project/portfolio/review_update', {
    method: 'post',
    data: params
  })
}

// 所有/我的 Portfolio Review - 列表
export async function getPortfolioReviews(params: any) {
  return request('/api/v2/project/portfolio/search_portfolio_review', {
    method: 'post',
    data: params
  })
}

interface ISearchPortfolioReviewProjectRequest {
  project_name: string
}
// portfolioReview切换项目
export async function searchPortfolioReviewProject(params: ISearchPortfolioReviewProjectRequest) {
  return request('/api/v2/project/portfolio/review_project_list', {
    method: 'GET',
    data: params
  })
}

interface IDelPortfolioReviewDraftRequest {
  project_id: number
  id: number
}
// portfolioReview删除草稿
export async function delPortfolioReviewDraft(params: IDelPortfolioReviewDraftRequest) {
  return request('/api/v2/project/portfolio/review_delete_draft', {
    method: 'POST',
    data: params
  })
}

// 获取Portfolio Review财务宽表数据
export async function getPortfolioReviewTable(params: any) {
  return request<CommonRes<TableDataType>>('/api/v2/project/portfolio_review_table_list', {
    method: 'GET',
    data: params
  })
}

// 编辑Portfolio Review财务宽表数据
export async function editPortfolioReviewTable(params: any) {
  return request('/api/v2/project/portfolio_review_table_save', {
    method: 'POST',
    data: params
  })
}

// 工作台 - Deal Portfolio列表
export async function getDealPortfolioList(params: any) {
  return request('/api/v2/project/search/deal_portfolio', {
    method: 'post',
    data: params
  })
}
// 中台运营 金标准
export interface IGetDealPortfolioListParams {
  addresses?: string
  category?: string
  channel_ids?: number[]
  circle_code?: string
  create_time_end?: string
  create_time_start?: string
  fund_id?: number
  is_gold_standard?: number
  page?: number
  pcode?: string
  pm_id?: number
  product_thesis_ids?: number[]
  project_name?: string
  size?: number
  sort?: string
  update_time_end?: string
  update_time_start?: string
}
export async function getDealPortfolioForLeads(params: IGetDealPortfolioListParams) {
  return request<CommonRes<IGetDealPortfolioForLeadsResponse>>(
    '/api/v2/project/search/deal_portfolio_for_leads',
    {
      method: 'post',
      data: params
    }
  )
}

interface IEnterPortfolioParams {
  memo_id: number
  financial_connection: number
  sponsor_uid: number
  company_structure_type: CompanyStructureType
}
// 进入Portfolio（状态）
export async function enterPortfolio(params: IEnterPortfolioParams) {
  return request('/api/v2/project/portfolio/enter', {
    method: 'post',
    data: params
  })
}

interface IReviewRollback {
  ids: number[]
}
// 还原PR提交状态
export async function reviewRollback(params: IReviewRollback) {
  return request('/api/v2/project/portfolio/review_rollback_submit', {
    method: 'post',
    data: params
  })
}

interface IPortfolioCaptableSaveRequest {
  captable: portfolioType.PortfolioCaptableHeader
}
// 保存Captable表单数据
export async function portfolioCaptableSave(params: IPortfolioCaptableSaveRequest) {
  return request('/api/v2/project/portfolio_captable_save', {
    method: 'post',
    data: params
  })
}

interface IGetPortfolioCaptableRequest {
  id: number
}
// 获取Captable表单数据
export async function getPortfolioCaptable(params: IGetPortfolioCaptableRequest) {
  return request('/api/v2/project/portfolio_captable_get', {
    method: 'get',
    data: params
  })
}

interface IGetPortfolioCaptableListRequest {
  project_id: number
  show_round_hide?: number
}
// 获取Captable表单数据
export async function getPortfolioCaptableList(params: IGetPortfolioCaptableListRequest) {
  return request('/api/v2/project/portfolio_captable_list', {
    method: 'get',
    data: params
  })
}

interface ICheckCaptableBlockIdRequest {
  id: number
  project_id: number
  block_id: string
}
// 获取Captable表单数据
export async function checkCaptableBlockId(params: ICheckCaptableBlockIdRequest) {
  return request('/api/v2/project/portfolio_captable_check_block_id', {
    method: 'get',
    data: params
  })
}

interface IDelPortfolioCaptableDataRequest {
  id: number
  // project_id: number
  // block_id: string
}
// 删除Captable表单数据
export async function delPortfolioCaptableData(params: IDelPortfolioCaptableDataRequest) {
  return request('/api/v2/project/portfolio_captable_delete', {
    method: 'post',
    data: params
  })
}

interface IUpdateCaptableCurrencyRequest {
  project_id: number
  calculation_currency: string
  list: {
    id: number
    exchange_rate: string
  }[]
}
// 删除Captable表单数据
export async function updateCaptableCurrency(params: IUpdateCaptableCurrencyRequest) {
  return request('/api/v2/project/portfolio_captable_update_calculation_currency', {
    method: 'post',
    data: params
  })
}

interface IGetPortfolioReviewTodoListRequest {
  project_id: number
}
// 获取portfolioReview的todolist列表数据
export async function getPortfolioReviewTodoList(params: IGetPortfolioReviewTodoListRequest) {
  return request<CommonRes<{ list: IPortfolioReviewTodo[] }>>(
    '/api/v2/project/portfolio/review_todo_list',
    {
      method: 'get',
      data: params
    }
  )
}

interface IAddPortfolioReviewTodoListRequest {
  todo: portfolioType.IPortfolioReviewTodo
}
// 新增portfolioReview的todo数据
export async function addPortfolioReviewTodoList(params: IAddPortfolioReviewTodoListRequest) {
  return request('/api/v2/project/portfolio/review_todo_add', {
    method: 'post',
    data: params
  })
}

interface IUpdatePortfolioReviewTodoListRequest {
  todo: portfolioType.IPortfolioReviewTodo
}
// 更新portfolioReview的todo数据
export async function updatePortfolioReviewTodoList(params: IUpdatePortfolioReviewTodoListRequest) {
  return request('/api/v2/project/portfolio/review_todo_update', {
    method: 'post',
    data: params
  })
}
interface IUpdatePortfolioReviewTodoListRequest {
  todo: portfolioType.IPortfolioReviewTodo
}
// portfolio review金标准设置信息获取
export async function getReviewGoldStandardSettingInfo(params: { project_id: number }) {
  return request<CommonRes<IGoldStandardSettingInfoResponse>>(
    '/api/v2/project/portfolio/review_gold_standard_setting_info_get',
    {
      method: 'get',
      data: params
    }
  )
}
export interface IUpdateReviewGoldStandardSettingParams {
  gold_standard_competitor_table?: IDynamicTable
  gold_standard_metrics_table?: IDynamicTable
  is_open_gold_standard_competitor?: number
  is_open_gold_standard_metrics?: number
  project_id?: number
}
//portfolio review金标准模板设置
export async function updateReviewGoldStandardSetting(
  params: IUpdateReviewGoldStandardSettingParams
) {
  return request('/api/v2/project/portfolio/review_gold_standard_setting', {
    method: 'post',
    data: params
  })
}
interface IPostInvestmentEventParams {
  post_investment_event?: IPortfolioPostInvestmentEventSave
}

// 新增投后事件
export async function apiAddPostInvestmentEvent(params: IPostInvestmentEventParams) {
  return request('/api/v2/project/portfolio/post_investment_event_add', {
    method: 'post',
    data: params
  })
}
// 修改投后事件
export async function apiUpdatePostInvestmentEvent(params: IPostInvestmentEventParams) {
  return request('/api/v2/project/portfolio/post_investment_event_update', {
    method: 'post',
    data: params
  })
}
// 删除投后事件
export async function apiDeletePostInvestmentEvent(params: { post_investment_event_id: number }) {
  return request('/api/v2/project/portfolio/post_investment_event_delete', {
    method: 'post',
    data: params
  })
}

// 标记完成投后事件
export async function apiCompletePostInvestmentMarking(params: {
  post_investment_event_id: number
}) {
  return request('/api/v2/project/portfolio/post_investment_marking_complete', {
    method: 'post',
    data: params
  })
}
// 取消标记投后事件
export async function apiCancelCompletePostInvestmentMarking(params: {
  post_investment_event_id: number
}) {
  return request('/api/v2/project/portfolio/post_investment_cancel_completed', {
    method: 'post',
    data: params
  })
}
// 获取投后事件
export async function apiGetPortfolioPostInvestmentEventList(params: { project_id: number }) {
  return request<CommonRes<{ post_investment_event_list: IPortfolioPostInvestmentEvent[] }>>(
    '/api/v2/project/portfolio/post_investment_event_list',
    {
      method: 'get',
      data: params
    }
  )
}
// pr演示获取投后事件
export async function apiGetPortfolioPostInvestmentEventListForPortfolioReview(params: {
  project_id: number
  nearly_months?: number
}) {
  return request<CommonRes<{ post_investment_event_list: IPortfolioPostInvestmentEvent[] }>>(
    '/api/v2/project/portfolio/post_investment_event_list_for_portfolio_review',
    {
      method: 'get',
      data: params
    }
  )
}

// 获取投后事件
export async function apiGetPortfolioReviewAttachmentFileList(params: { project_id: number }) {
  return request<CommonRes<{ list: IPortfolioReviewAttachmentFile[] }>>(
    '/api/v2/project/portfolio/review_attachment_file_list',
    {
      method: 'get',
      data: params
    }
  )
}
// 获取退出Memo
export async function apiGetMemoListForPortfolioExit(params: { project_id: number }) {
  return request<CommonRes<{ list: IMemoListForPortfolioExitReplyMemo[] }>>(
    '/api/v2/project/memo/list_for_portfolio_exit',
    {
      method: 'get',
      data: params
    }
  )
}

// 获取Portfolio退出
export async function apiGetPortfolioExitList(params: { project_id: number }) {
  return request<CommonRes<{ list: IPortfolioExit[] }>>(
    '/api/v2/project/portfolio/portfolio_exit_list',
    {
      method: 'get',
      data: params
    }
  )
}
// 添加Portfolio退出方案
export async function apiAddPortfolioExit(params: { exit: IPortfolioExitSave }) {
  return request('/api/v2/project/portfolio/portfolio_exit_add', {
    method: 'post',
    data: params
  })
}
// 删除Portfolio退出方案
export async function apiDeletePortfolioExit(params: { id: number }) {
  return request('/api/v2/project/portfolio/portfolio_exit_delete', {
    method: 'post',
    data: params
  })
}

interface IPortfolioOperateCommonSearchParams {
  project_name?: string
  pcode?: string
  portfolio_status?: number
  category?: string
  pm_uid?: number
  sponsor_uid?: number
  page?: number
  size?: number
}

interface IPortfolioReviewTodoSearchListParams extends IPortfolioOperateCommonSearchParams {
  todo_state?: number
  create_time_start?: number
  create_time_end?: number
}

/** Portfolio Review Todo搜索列表 */
export async function apiPortfolioReviewTodoSearchList(params: IPortfolioReviewTodoSearchListParams) {
  return request<CommonRes<portfolioType.IPortfolioReviewTodoSearchListReply>>(
    '/api/v2/project/portfolio/review_todo_search_list',
    {
      method: 'post',
      data: params
    }
  )
}

interface IPortfolioPostInvestmentEventSearchListParams extends IPortfolioOperateCommonSearchParams {
  event_type?: number
  due_date_start?: number
  due_date_end?: number
}

/** Portfolio投后事件搜索列表 */
export async function apiPortfolioPostInvestmentEventSearchList(
  params: IPortfolioPostInvestmentEventSearchListParams
) {
  return request<CommonRes<portfolioType.IPortfolioPostInvestmentEventSearchListReply>>(
    '/api/v2/project/portfolio/post_investment_event_search_list',
    {
      method: 'post',
      data: params
    }
  )
}

interface IPortfolioExitSearchListParams extends IPortfolioOperateCommonSearchParams {
  exit_method?: number
  exit_status?: number
  start_time_start?: number
  start_time_end?: number
}

/** Portfolio退出搜索列表 */
export async function apiPortfolioExitSearchList(params: IPortfolioExitSearchListParams) {
  return request<CommonRes<portfolioType.IPortfolioExitSearchListReply>>(
    '/api/v2/project/portfolio/portfolio_exit_search_list',
    {
      method: 'post',
      data: params
    }
  )
}
